'use client';

import React, { useState, useEffect } from 'react';
import { Service, getAllSlots, SlotInfo, formatDuration } from '@/lib/bookingStore';

interface Props {
  selectedServices: Service[];
  selectedDate: string;
  selectedTime: string;
  onSelect: (time: string) => void;
  onNext: () => void;
  onBack: () => void;
}

export default function AgendarStepTimeSlots({ selectedServices, selectedDate, selectedTime, onSelect, onNext, onBack }: Props) {
  const [slots, setSlots] = useState<SlotInfo[]>([]);

  const totalDuration = selectedServices.reduce((sum, s) => sum + s.duration, 0);

  useEffect(() => {
    if (selectedDate) {
      setSlots(getAllSlots(selectedDate, totalDuration));
    }
  }, [selectedDate, totalDuration]);

  const formatDateDisplay = (dateStr: string) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-').map(Number);
    const months = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
    const days = ['Domingo','Segunda','Terça','Quarta','Quinta','Sexta','Sábado'];
    const date = new Date(y, m - 1, d);
    return `${days[date.getDay()]}, ${d} de ${months[m - 1]}`;
  };

  const availableCount = slots.filter(s => s.available).length;

  return (
    <div className="space-y-8">
      <div>
        <span className="gold-line mb-4 block" />
        <h2 className="font-serif text-3xl lg:text-4xl font-light text-dark-brown mb-2">
          Escolha o horário
        </h2>
        <p className="text-sm text-foreground/60">
          {formatDateDisplay(selectedDate)} · Sessão de <span className="font-semibold text-burnt-rose">{formatDuration(totalDuration)}</span>
        </p>
      </div>

      {slots.length === 0 ? (
        <div className="bg-white border border-border rounded-2xl p-10 text-center">
          <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-foreground/40">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
          </div>
          <p className="font-serif text-xl text-dark-brown mb-2">Sem horários disponíveis</p>
          <p className="text-sm text-foreground/60 mb-6">Não há horários disponíveis para esta data.</p>
          <button
            onClick={onBack}
            className="text-sm text-burnt-rose hover:underline font-medium"
          >
            ← Escolher outra data
          </button>
        </div>
      ) : (
        <div>
          <p className="text-xs tracking-wide uppercase text-foreground/50 font-medium mb-4">
            {availableCount} horário{availableCount !== 1 ? 's' : ''} disponível{availableCount !== 1 ? 'is' : ''} · 08:00 – 18:00
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2.5">
            {slots.map(slot => (
              <button
                key={slot.time}
                onClick={() => slot.available && onSelect(slot.time)}
                disabled={!slot.available}
                title={!slot.available ? 'Horário indisponível' : undefined}
                className={`py-3 rounded-xl text-sm font-semibold transition-all duration-200 active:scale-95 ${
                  selectedTime === slot.time
                    ? 'bg-burnt-rose text-white shadow-md'
                    : slot.available
                    ? 'bg-white border border-border text-foreground hover:border-burnt-rose hover:text-burnt-rose hover:bg-blush/30 cursor-pointer'
                    : 'bg-muted/50 border border-border/40 text-foreground/25 cursor-not-allowed line-through'
                }`}
              >
                {slot.time}
              </button>
            ))}
          </div>
          <p className="text-xs text-foreground/40 mt-3">
            <span className="inline-block w-3 h-3 rounded bg-muted/50 border border-border/40 mr-1 align-middle" />
            Horários riscados estão ocupados
          </p>
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={onBack}
          className="flex-1 py-4 rounded-full border-2 border-border text-sm font-semibold tracking-wide uppercase text-foreground/60 hover:border-burnt-rose hover:text-burnt-rose transition-all duration-200"
        >
          Voltar
        </button>
        <button
          onClick={onNext}
          disabled={!selectedTime}
          className={`flex-grow-[2] py-4 rounded-full font-semibold text-sm tracking-[0.1em] uppercase transition-all duration-300 ${
            selectedTime ? 'btn-primary' : 'bg-muted text-foreground/40 cursor-not-allowed'
          }`}
        >
          Continuar
        </button>
      </div>
    </div>
  );
}
